// src/components/dashboard/ProfileSection.tsx - GÜNCELLENMIŞ (Organizasyon bilgisi eklenmiş)
import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import { apiService, UserProfile } from '../../services/api'
import { formatCurrency } from '../../utils/format'
import toast from 'react-hot-toast'
import { 
  Loader2, 
  Lock, 
  X, 
  Eye, 
  EyeOff, 
  CheckCircle, 
  Shield,
  User,
  Phone,
  Mail,
  Calendar,
  Award,
  Package,
  DollarSign,
  Building2,
  Copy
} from 'lucide-react'

interface ProfileSectionProps {
  stats: {
    bonusLevel: number;
    totalOrders: number;
    totalEarnings: number;
  };
}

export default function ProfileSection({ stats }: ProfileSectionProps) {
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: ''
  })

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  const [formErrors, setFormErrors] = useState({
    fullName: '',
    phoneNumber: ''
  })

  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    try {
      setLoading(true)
      const data = await apiService.getUserProfile()
      setProfile(data)
      setFormData({
        fullName: data.fullName,
        phoneNumber: data.phoneNumber || ''
      })
    } catch (error) {
      console.error('Profile fetch error:', error)
      toast.error('Profil bilgileri yüklenemedi')
    } finally {
      setLoading(false)
    }
  }

  const validateProfileForm = () => {
    const errors = {
      fullName: '',
      phoneNumber: ''
    }
    let isValid = true

    // Full name validation
    if (!formData.fullName.trim()) {
      errors.fullName = 'Ad Soyad gereklidir'
      isValid = false
    } else if (formData.fullName.trim().length < 2) {
      errors.fullName = 'Ad Soyad en az 2 karakter olmalıdır'
      isValid = false
    } else if (formData.fullName.trim().length > 100) {
      errors.fullName = 'Ad Soyad en fazla 100 karakter olabilir'
      isValid = false
    }

    // Telefon: ülke kodlu ve tam olmalı (TR +90, ABD +1, UK +44, DE +49)
    const normalizedPhone = formData.phoneNumber.replace(/[\s\-().]/g, '')
    if (!/^(\+90\d{10}|\+1\d{10}|\+44\d{9,10}|\+49\d{9,11})$/.test(normalizedPhone)) {
      errors.phoneNumber = 'Ülke koduyla tam bir telefon girin (ör. +90 5XX XXX XX XX, +1, +44, +49)'
      isValid = false
    }

    setFormErrors(errors)
    return isValid
  }

  const validatePasswordForm = () => {
    const errors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }
    let isValid = true

    if (!passwordData.currentPassword.trim()) {
      errors.currentPassword = 'Mevcut şifre gereklidir'
      isValid = false
    }

    if (!passwordData.newPassword.trim()) {
      errors.newPassword = 'Yeni şifre gereklidir'
      isValid = false
    } else if (passwordData.newPassword.length < 6) {
      errors.newPassword = 'Şifre en az 6 karakter olmalıdır'
      isValid = false
    }

    if (!passwordData.confirmPassword.trim()) {
      errors.confirmPassword = 'Şifre tekrarı gereklidir'
      isValid = false
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Şifreler eşleşmiyor'
      isValid = false
    }

    setPasswordErrors(errors)
    return isValid
  }

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateProfileForm()) {
      return
    }

    try {
      setUpdating(true)
      await apiService.updateUserProfile({
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber.replace(/[\s\-().]/g, '')
      })
      toast.success('Profil başarıyla güncellendi!')
      fetchProfile()
    } catch (error) {
      console.error('Profile update error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Profil güncellenirken hata oluştu'
      toast.error(errorMessage)
    } finally {
      setUpdating(false)
    }
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validatePasswordForm()) {
      return
    }

    try {
      setUpdating(true)
      await apiService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword
      })
      toast.success('Şifre başarıyla değiştirildi!')
      setShowPasswordModal(false)
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
      setPasswordErrors({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
    } catch (error) {
      console.error('Password change error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Şifre değiştirilirken hata oluştu'
      toast.error(errorMessage)
    } finally {
      setUpdating(false)
    }
  }

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false)
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    })
    setPasswordErrors({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    })
    setShowCurrentPassword(false)
    setShowNewPassword(false)
    setShowConfirmPassword(false)
  }

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Bilinmiyor'
    try {
      return new Date(dateString).toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    } catch {
      return 'Bilinmiyor'
    }
  }

  const formatMemberSince = (dateString: string | undefined) => {
    if (!dateString) return 'Bilinmiyor'
    try {
      return new Date(dateString).toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long'
      })
    } catch {
      return 'Bilinmiyor'
    }
  }

  // YENİ: Kodu kopyalama fonksiyonu
  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code)
    toast.success(`Kod kopyalandı: ${code}`)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-orange-500" />
          <p className="text-gray-600 font-medium">Profil yükleniyor...</p>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center py-12">
        <Card className="glass border-orange-200 shadow-lg p-8 text-center">
          <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg font-medium mb-4">Profil bilgileri yüklenemedi</p>
          <Button onClick={fetchProfile} className="bg-orange-500 hover:bg-orange-600">
            Tekrar Dene
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Personal Information */}
          <Card className="glass border-orange-200 shadow-lg">
            <CardHeader>
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-lg font-semibold text-gray-800">
                    Kişisel Bilgiler
                  </CardTitle>
                  <CardDescription className="text-gray-600">
                    Profil bilgilerinizi görüntüleyin ve güncelleyin
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                {/* Email (disabled) */}
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-600 mb-2">
                    <Mail className="w-4 h-4 mr-2" />
                    E-posta Adresi
                  </label>
                  <Input
                    type="email"
                    value={profile.email}
                    disabled
                    className="bg-gray-100 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1 flex items-center">
                    <Shield className="w-3 h-3 mr-1" />
                    Güvenlik nedeniyle e-posta adresi değiştirilemez
                  </p>
                </div>
                
                {/* Full Name */}
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-600 mb-2">
                    <User className="w-4 h-4 mr-2" />
                    Ad Soyad
                  </label>
                  <Input
                    type="text"
                    placeholder="Adınızı ve soyadınızı girin"
                    value={formData.fullName}
                    onChange={(e) => {
                      setFormData({ ...formData, fullName: e.target.value })
                      if (formErrors.fullName) {
                        setFormErrors({ ...formErrors, fullName: '' })
                      }
                    }}
                    disabled={updating}
                    required
                    className={formErrors.fullName ? 'border-red-500' : ''}
                  />
                  {formErrors.fullName && (
                    <p className="text-xs text-red-600 mt-1 flex items-center">
                      <X className="w-3 h-3 mr-1" />
                      {formErrors.fullName}
                    </p>
                  )}
                </div>
                
                {/* Phone Number */}
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-600 mb-2">
                    <Phone className="w-4 h-4 mr-2" />
                    Telefon Numarası
                  </label>
                  <Input
                    type="tel"
                    placeholder="+90 5XX XXX XX XX · +1 · +44 · +49"
                    value={formData.phoneNumber}
                    onChange={(e) => {
                      setFormData({ ...formData, phoneNumber: e.target.value })
                      if (formErrors.phoneNumber) {
                        setFormErrors({ ...formErrors, phoneNumber: '' })
                      }
                    }}
                    disabled={updating}
                    className={formErrors.phoneNumber ? 'border-red-500' : ''}
                  />
                  {formErrors.phoneNumber && (
                    <p className="text-xs text-red-600 mt-1 flex items-center">
                      <X className="w-3 h-3 mr-1" />
                      {formErrors.phoneNumber}
                    </p>
                  )}
                </div>
                
                {/* Update Button */}
                <Button 
                  type="submit" 
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  disabled={updating}
                >
                  {updating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Güncelleniyor...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Profili Güncelle
                    </>
                  )}
                </Button>
              </form>

              {/* Change Password Button */}
              <Button 
                onClick={() => setShowPasswordModal(true)}
                variant="outline"
                className="w-full mt-4 border-orange-300 text-orange-600 hover:bg-orange-50"
              >
                <Lock className="w-4 h-4 mr-2" />
                Şifre Değiştir
              </Button>
            </CardContent>
          </Card>

          {/* Account Statistics */}
          <Card className="glass border-orange-200 shadow-lg">
            <CardHeader>
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 flex items-center justify-center">
                  <Award className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-lg font-semibold text-gray-800">
                    Hesap İstatistikleri
                  </CardTitle>
                  <CardDescription className="text-gray-600">
                    Hesabınızın genel durumu
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4">
                {/* Total Orders */}
                <div className="p-4 border border-orange-200 rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-2 mb-2">
                    <Package className="w-4 h-4 text-blue-600" />
                    <span className="text-xs text-gray-600">Toplam Sipariş</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">
                    {profile.stats?.totalOrders || 0}
                  </p>
                </div>

                {/* Delivered Orders */}
                <div className="p-4 border border-orange-200 rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-xs text-gray-600">Teslim Edilen</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">
                    {profile.stats?.deliveredOrders || 0}
                  </p>
                </div>

{/* Total Earnings */}
<div className="col-span-2 p-4 border-2 border-green-200 bg-green-50 rounded-lg">
  <div className="flex items-center space-x-2 mb-2">
    <DollarSign className="w-5 h-5 text-green-600" />
    <span className="text-sm font-medium text-gray-700">Toplam Kazanç</span>
  </div>
  <p className="text-3xl font-bold text-green-600">
    {formatCurrency(profile?.stats?.totalEarnings ?? 0)}
  </p>
</div>
              </div>

              {/* Member Since */}
              <div className="flex items-center justify-between pt-4 border-t border-orange-200">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-gray-600" />
                  <span className="text-sm text-gray-600">Üye Olma Tarihi</span>
                </div>
                <span className="font-medium text-gray-800">
                  {formatMemberSince(profile.stats?.memberSince)}
                </span>
              </div>

              {/* Account Status */}
              <div className="flex items-center justify-between pb-2">
                <div className="flex items-center space-x-2">
                  <Shield className="w-4 h-4 text-gray-600" />
                  <span className="text-sm text-gray-600">Hesap Durumu</span>
                </div>
                <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                  profile.isActive 
                    ? 'bg-green-100 text-green-700 border border-green-200' 
                    : 'bg-red-100 text-red-700 border border-red-200'
                }`}>
                  {profile.isActive ? '✓ Aktif' : '✗ Pasif'}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

{/* YENİ: Basit Referans Kodu Gösterimi - Sadece organizasyona kayıtlı kullanıcılar için */}
{profile.organizationCode && (
  <motion.div 
    initial={{ opacity: 0, y: 20 }} 
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.2 }}
    className="mt-6"
  >
    <Card className="glass border-orange-200 shadow-lg">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-orange-600" />
            <div>
              <span className="text-sm text-gray-600">Referans Kodu: </span>
              <span className="font-mono text-lg font-bold text-orange-600">
                {profile.organizationCode}
              </span>
            </div>
          </div>
          <button
            onClick={() => copyCode(profile.organizationCode!)}
            className="p-2 hover:bg-orange-50 rounded-lg transition-colors"
            title="Kodu Kopyala"
          >
            <Copy className="w-5 h-5 text-orange-600" />
          </button>
        </div>
      </CardContent>
    </Card>
  </motion.div>
        )}
      </motion.div>

      {/* Password Change Modal */}
      <AnimatePresence>
        {showPasswordModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={handleClosePasswordModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full"
            >
              <Card className="border-0">
                <CardHeader className="border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 flex items-center justify-center">
                        <Lock className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-semibold text-gray-800">
                          Şifre Değiştir
                        </CardTitle>
                        <CardDescription className="text-gray-600">
                          Güvenli bir şifre seçin
                        </CardDescription>
                      </div>
                    </div>
                    <button
                      onClick={handleClosePasswordModal}
                      className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    {/* Current Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">
                        Mevcut Şifre
                      </label>
                      <div className="relative">
                        <Input
                          type={showCurrentPassword ? 'text' : 'password'}
                          placeholder="Mevcut şifrenizi girin"
                          value={passwordData.currentPassword}
                          onChange={(e) => {
                            setPasswordData({ ...passwordData, currentPassword: e.target.value })
                            if (passwordErrors.currentPassword) {
                              setPasswordErrors({ ...passwordErrors, currentPassword: '' })
                            }
                          }}
                          disabled={updating}
                          required
                          className={passwordErrors.currentPassword ? 'border-red-500 pr-10' : 'pr-10'}
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {passwordErrors.currentPassword && (
                        <p className="text-xs text-red-600 mt-1 flex items-center">
                          <X className="w-3 h-3 mr-1" />
                          {passwordErrors.currentPassword}
                        </p>
                      )}
                    </div>

                    {/* New Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">
                        Yeni Şifre
                      </label>
                      <div className="relative">
                        <Input
                          type={showNewPassword ? 'text' : 'password'}
                          placeholder="En az 6 karakter"
                          value={passwordData.newPassword}
                          onChange={(e) => {
                            setPasswordData({ ...passwordData, newPassword: e.target.value })
                            if (passwordErrors.newPassword) {
                              setPasswordErrors({ ...passwordErrors, newPassword: '' })
                            }
                          }}
                          disabled={updating}
                          required
                          minLength={6}
                          className={passwordErrors.newPassword ? 'border-red-500 pr-10' : 'pr-10'}
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {passwordErrors.newPassword && (
                        <p className="text-xs text-red-600 mt-1 flex items-center">
                          <X className="w-3 h-3 mr-1" />
                          {passwordErrors.newPassword}
                        </p>
                      )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">
                        Yeni Şifre (Tekrar)
                      </label>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="Yeni şifrenizi tekrar girin"
                          value={passwordData.confirmPassword}
                          onChange={(e) => {
                            setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                            if (passwordErrors.confirmPassword) {
                              setPasswordErrors({ ...passwordErrors, confirmPassword: '' })
                            }
                          }}
                          disabled={updating}
                          required
                          minLength={6}
                          className={passwordErrors.confirmPassword ? 'border-red-500 pr-10' : 'pr-10'}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {passwordErrors.confirmPassword && (
                        <p className="text-xs text-red-600 mt-1 flex items-center">
                          <X className="w-3 h-3 mr-1" />
                          {passwordErrors.confirmPassword}
                        </p>
                      )}
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleClosePasswordModal}
                        disabled={updating}
                        className="flex-1"
                      >
                        İptal
                      </Button>
                      <Button
                        type="submit"
                        disabled={updating}
                        className="flex-1 bg-orange-500 hover:bg-orange-600"
                      >
                        {updating ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Değiştiriliyor...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Şifreyi Değiştir
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}